import React from 'react';
import { Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useSeoMeta } from '../hooks/useSeoMeta';
import { OG_IMAGE_URL } from './home-redesign/cityLinks';
import Footer from '../components/Footer';

const DeleteUser = () => {
  const { currentLanguage } = useLanguage();

  const copy = {
    en: {
      backHome: '← Back to Home',
      title: 'Delete your Chisme account',
      seoDescription:
        'How to permanently delete your Chisme account from the app or by contacting support.',
      lead:
        'You can delete your account directly in the Chisme app. This action is permanent and cannot be undone.',
      stepsTitle: 'How to delete your account in the app',
      steps: [
        'Open the Chisme app and go to Profile.',
        'Tap Settings.',
        'Scroll to the bottom and tap Delete account.',
        'Confirm the deletion.'
      ],
      whatHappensTitle: 'What happens next',
      whatHappens: [
        'Your profile and messages will be scheduled for deletion.',
        'Residual backups may be retained for a short period for security and legal compliance.',
        'Active subscriptions must be managed in your App Store / Google Play account.'
      ],
      subsTitle: 'Manage your subscription',
      iosSubs: 'Manage on Apple (App Store)',
      androidSubs: 'Manage on Google Play',
      trouble: 'Having trouble?',
      contact:
        'If you cannot access the app or need help, email us at ',
      contactEmail: 'privacy@chisme.social'
    },
    es: {
      backHome: '← Volver al inicio',
      title: 'Eliminar tu cuenta de Chisme',
      seoDescription:
        'Cómo eliminar permanentemente tu cuenta de Chisme desde la app o contactando con soporte.',
      lead:
        'Puedes eliminar tu cuenta directamente en la app de Chisme. Esta acción es permanente y no se puede deshacer.',
      stepsTitle: 'Cómo eliminar tu cuenta en la app',
      steps: [
        'Abre la app de Chisme y ve a Perfil.',
        'Toca Configuración.',
        'Desplázate al final y toca Eliminar cuenta.',
        'Confirma la eliminación.'
      ],
      whatHappensTitle: 'Qué sucede después',
      whatHappens: [
        'Tu perfil y tus mensajes se programarán para su eliminación.',
        'Podrían conservarse copias de seguridad residuales por un período corto por motivos legales y de seguridad.',
        'Las suscripciones activas deben gestionarse en tu cuenta de App Store / Google Play.'
      ],
      subsTitle: 'Gestionar tu suscripción',
      iosSubs: 'Gestionar en Apple (App Store)',
      androidSubs: 'Gestionar en Google Play',
      trouble: '¿Tienes problemas?',
      contact:
        'Si no puedes acceder a la app o necesitas ayuda, escríbenos a ',
      contactEmail: 'privacy@chisme.social'
    }
  };

  const t = copy[currentLanguage] || copy.en;

  useSeoMeta({
    title: `${t.title} | Chisme`,
    description: t.seoDescription,
    url: 'https://chisme.social/deleteuser',
    image: OG_IMAGE_URL,
    locale: currentLanguage === 'en' ? 'en' : 'es',
    noindex: true,
  });

  return (
    <div className="min-h-screen bg-[#F2E8F0] flex flex-col">
      <div className="flex flex-1 flex-col items-center px-6 py-12 w-full">
        <div className="w-full max-w-2xl bg-white shadow-md rounded-xl p-8 space-y-6">
        <div className="flex items-center">
          <Link to="/" className="text-pink-600 font-medium hover:underline">
            {t.backHome}
          </Link>
        </div>

        {/* Content */}
        <h1 className="text-2xl font-bold text-gray-900">{t.title}</h1>
        <p className="text-gray-700">{t.lead}</p>

        <div>
          <h2 className="font-semibold text-gray-900 mt-4 mb-2">{t.stepsTitle}</h2>
          <ol className="list-decimal list-inside space-y-1 text-gray-700">
            {t.steps.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ol>
        </div>

        <div>
          <h2 className="font-semibold text-gray-900 mt-4 mb-2">{t.whatHappensTitle}</h2>
          <ul className="list-disc list-inside space-y-1 text-gray-700">
            {t.whatHappens.map((s, i) => (
              <li key={i}>{s}</li>
            ))}
          </ul>
        </div>

        <div>
          <h2 className="font-semibold text-gray-900 mt-4 mb-2">{t.subsTitle}</h2>
          <div className="flex flex-wrap gap-3">
            <a
              href="https://support.apple.com/HT202039"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-600 underline"
            >
              {t.iosSubs}
            </a>
            <a
              href="https://support.google.com/googleplay/answer/7018481"
              target="_blank"
              rel="noopener noreferrer"
              className="text-pink-600 underline"
            >
              {t.androidSubs}
            </a>
          </div>
        </div>

        <div className="pt-2">
          <p className="text-gray-700">
            <span className="font-medium">{t.trouble}</span>{' '}
            {t.contact}
            <a href={`mailto:${t.contactEmail}`} className="text-pink-600 underline">
              {t.contactEmail}
            </a>
            .
          </p>
        </div>
        </div>
      </div>
      <Footer />
    </div>
  );
};

export default DeleteUser;
